import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { patchProfileSettings } from '../services/cloudService';
import { DashboardLayout, normalizeLayout, resolveLayout } from '../components/dashboard/dashboardLayout';
import { createLayoutSaver, type LayoutSaveState } from '../components/dashboard/dashboardLayoutSaver';

export type { LayoutSaveState } from '../components/dashboard/dashboardLayoutSaver';

/**
 * Das persoenliche Dashboard-Layout — auf dem Server gespeichert.
 *
 * Quelle der Wahrheit ist `profiles.settings.dashboardLayout` (flach
 * gemischt ueber patchProfileSettings, damit kein anderer Settings-Block
 * verloren geht). Der Browser haelt eine Kopie fuer den sofortigen Aufbau;
 * zwischen den Staenden entscheidet der Zeitstempel (resolveLayout), sodass
 * ein Umbau am PC auch am Handy ankommt und umgekehrt.
 *
 * Gespeichert wird verzoegert (SAVE_DELAY_MS nach der letzten Aenderung):
 * beim Ziehen entstehen sonst viele Schreibvorgaenge hintereinander. Beim
 * Verlassen der Seite wird ein ausstehender Stand sofort vorgemerkt. Das
 * eigentliche Schreiben uebernimmt der Speicher-Baustein auf Modulebene
 * (dashboardLayoutSaver): nacheinander, nie parallel, und ein gescheiterter
 * Stand bleibt vorgemerkt — auch wenn das Dashboard inzwischen zu ist.
 */

export const SAVE_DELAY_MS = 800;

const saver = createLayoutSaver((userId, layout) => patchProfileSettings(userId, { dashboardLayout: layout }));

const storageKey = (userId: string) => `dashboard.layout.v1.${userId}`;

/** Juengster Stand dieser Sitzung je Nutzer (das Profil in App wird nach dem Speichern nicht neu geladen). */
const sessionLayouts = new Map<string, DashboardLayout>();

const readLocal = (userId: string): unknown => {
    try {
        const raw = window.localStorage.getItem(storageKey(userId));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeLocal = (userId: string, layout: DashboardLayout) => {
    try { window.localStorage.setItem(storageKey(userId), JSON.stringify(layout)); } catch { /* privat/voll — Server bleibt Quelle */ }
};

const serverLayoutOf = (settings: unknown): unknown =>
    settings && typeof settings === 'object' ? (settings as Record<string, unknown>).dashboardLayout : undefined;

const noopSubscribe = () => () => {};
const idleStatus = (): LayoutSaveState => 'idle';

export interface UseDashboardLayoutResult {
    layout: DashboardLayout;
    /** Neuer Stand (Wert oder Funktion); wird sofort angezeigt und verzoegert gespeichert. */
    setLayout: (next: DashboardLayout | ((prev: DashboardLayout) => DashboardLayout)) => void;
    saveState: LayoutSaveState;
    /** Gescheitertes Speichern erneut versuchen. */
    retrySave: () => void;
}

export const useDashboardLayout = (
    userId: string | null | undefined,
    profileSettings: unknown,
): UseDashboardLayoutResult => {
    const serverRaw = serverLayoutOf(profileSettings);
    const serverStamp = normalizeLayout(serverRaw)?.updatedAt ?? null;

    const [layout, setLayoutState] = useState<DashboardLayout>(() =>
        resolveLayout(serverRaw, userId ? readLocal(userId) : null, userId ? sessionLayouts.get(userId) : null));

    const subscribe = useCallback((fn: () => void) => (userId ? saver.subscribe(userId, fn) : noopSubscribe()), [userId]);
    const getStatus = useCallback(() => (userId ? saver.status(userId) : idleStatus()), [userId]);
    const saveState = useSyncExternalStore(subscribe, getStatus, getStatus);

    // Waehrend der Verzoegerung zeigt die Leiste schon „Speichert…" — sonst
    // stuende bis zum Ablauf noch „Gespeichert" vom letzten Mal da.
    const [waiting, setWaiting] = useState(false);

    const layoutRef = useRef(layout);
    const pendingRef = useRef<DashboardLayout | null>(null);
    const timerRef = useRef<number | null>(null);
    const userRef = useRef(userId);

    // Nutzerwechsel oder frisch geladenes Profil: neu entscheiden. Jeder
    // eigene Umbau steht in Sitzung und Browser, also gewinnt ein juengerer
    // Serverstand (anderes Geraet), ein aelterer nicht. Ist der gewaehlte
    // Stand juenger als der Server und noch nie bestaetigt worden (Speichern
    // gescheitert, Seite danach verlassen oder neu geladen), geht er jetzt hoch.
    useEffect(() => {
        userRef.current = userId;
        if (!userId) return;
        const next = resolveLayout(serverRaw, readLocal(userId), sessionLayouts.get(userId));
        if (JSON.stringify(next) !== JSON.stringify(layoutRef.current)) {
            layoutRef.current = next;
            setLayoutState(next);
        }
        if (saver.needsPush(userId, next, serverStamp)) saver.request(userId, next);
    // serverRaw ist ein Objekt aus dem Profil — massgeblich ist sein Zeitstempel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, serverStamp]);

    const flush = useCallback(() => {
        if (timerRef.current !== null) { window.clearTimeout(timerRef.current); timerRef.current = null; }
        const uid = userRef.current;
        const value = pendingRef.current;
        pendingRef.current = null;
        setWaiting(false);
        if (uid && value) saver.request(uid, value);
    }, []);

    const setLayout = useCallback<UseDashboardLayoutResult['setLayout']>((next) => {
        const uid = userRef.current;
        const value = typeof next === 'function' ? next(layoutRef.current) : next;
        if (value === layoutRef.current) return;
        layoutRef.current = value;
        setLayoutState(value);
        if (!uid) return;
        sessionLayouts.set(uid, value);
        writeLocal(uid, value);
        pendingRef.current = value;
        setWaiting(true);
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(flush, SAVE_DELAY_MS);
    }, [flush]);

    const retrySave = useCallback(() => {
        const uid = userRef.current;
        if (uid) saver.retry(uid, layoutRef.current);
    }, []);

    // Seite verlassen oder Dashboard schliessen: ausstehenden Stand sofort vormerken.
    useEffect(() => {
        const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
        window.addEventListener('pagehide', flush);
        document.addEventListener('visibilitychange', onHide);
        return () => {
            window.removeEventListener('pagehide', flush);
            document.removeEventListener('visibilitychange', onHide);
            flush();
        };
    }, [flush]);

    return { layout, setLayout, saveState: waiting && saveState !== 'error' ? 'saving' : saveState, retrySave };
};
